import { Col, Row } from 'antd';
import { useFormikContext } from 'formik';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type InsurerDetailsDataFragment } from '../../../../api/fragments/InsurerDetailsData';
import { InsurerIntegrationProvider, type InsurerSettings, ModuleRole } from '../../../../api/types';
import DealershipPriceDisclaimerField from '../../../../components/fields/DealershipFields/DealershipPriceDisclaimer';
import ModuleSelectField from '../../../../components/fields/ModuleSelectField';
import CollapsibleWrapper, { Panel } from '../../../../components/wrappers/CollapsibleWrapper';
import FormFields from '../../../../themes/admin/Fields/FormFields';
import useMarkDownInfoTooltip from '../../../../utilities/useMarkDownInfoTooltip';
import useSystemSwitchData from '../../../../utilities/useSystemSwitchData';

const colSpan = { lg: 8, md: 12, xs: 24 };

export type InsurerDetailsFormProps = {
    updated: string;
    insurer: InsurerDetailsDataFragment;
    extra: JSX.Element;
    offset: string;
    disabled?: boolean;
};

const EditInsurerDetailsForm = ({ insurer, extra, updated, offset, disabled = false }: InsurerDetailsFormProps) => {
    const { t } = useTranslation(['insurerDetails', 'common']);
    const markdownInfoTooltip = useMarkDownInfoTooltip();

    const { yesNoSwitch } = useSystemSwitchData();

    const { values, setFieldValue } = useFormikContext<InsurerSettings>();
    useEffect(() => {
        if (!values.sendEmailToCustomer) {
            setFieldValue('sendPDFToCustomer', false);
        }
    }, [setFieldValue, values.sendEmailToCustomer]);
    const showCommentsField = useMemo(
        () =>
            [InsurerIntegrationProvider.Email, InsurerIntegrationProvider.Eazy].some(
                integration => integration === insurer?.integration?.provider
            ),
        [insurer?.integration?.provider]
    );

    return (
        <CollapsibleWrapper defaultActiveKey={t('insurerDetails:cardTitles.insurerDetails')}>
            <Panel
                key={t('insurerDetails:cardTitles.insurerDetails')}
                extra={extra}
                header={t('insurerDetails:cardTitles.insurerDetails')}
            >
                <Row gutter={14}>
                    <Col {...colSpan}>
                        <FormFields.InputField
                            {...t('insurerDetails:fields.name', { returnObjects: true })}
                            disabled={disabled}
                            name="displayName"
                            required
                        />
                    </Col>
                    <Col {...colSpan}>
                        <FormFields.TranslatedInputField
                            {...t('insurerDetails:fields.legalName', { returnObjects: true })}
                            disabled={disabled}
                            name="legalName"
                            required
                        />
                    </Col>
                    <Col {...colSpan}>
                        <FormFields.InputField
                            {...t('insurerDetails:fields.email', { returnObjects: true })}
                            disabled={disabled}
                            name="email"
                            required
                        />
                    </Col>
                    <Col {...colSpan}>
                        <FormFields.InputNumberField
                            {...t('insurerDetails:fields.orderNo', { returnObjects: true })}
                            disabled={disabled}
                            name="order"
                            required
                        />
                    </Col>
                    <Col {...colSpan}>
                        <FormFields.SwitchField
                            {...t('insurerDetails:fields.sendEmailToCustomer', { returnObjects: true })}
                            disabled={disabled}
                            name="sendEmailToCustomer"
                            {...yesNoSwitch}
                            required
                        />
                    </Col>
                    {values.sendEmailToCustomer && (
                        <Col {...colSpan}>
                            <FormFields.SwitchField
                                {...t('insurerDetails:fields.sendPDFToCustomer', { returnObjects: true })}
                                disabled={disabled}
                                name="sendPDFToCustomer"
                                {...yesNoSwitch}
                                required
                            />
                        </Col>
                    )}
                    <Col lg={8} xs={24}>
                        <DealershipPriceDisclaimerField
                            {...t('insurerDetails:fields.insuranceDisclaimer', { returnObjects: true })}
                            disabled={disabled}
                            disclaimerKind="insuranceDisclaimer"
                            name="insuranceDisclaimer"
                            tooltip={markdownInfoTooltip}
                            isMultipleEntry
                        />
                    </Col>
                    <Col {...colSpan}>
                        <ModuleSelectField
                            {...t('insurerDetails:fields.submission', { returnObjects: true })}
                            companyId={insurer.module.companyId}
                            disabled={disabled}
                            moduleRole={ModuleRole.Signing}
                            name="submissionApprovalModuleId"
                            addNoneOption
                        />
                    </Col>
                    <Col {...colSpan}>
                        <ModuleSelectField
                            {...t('insurerDetails:fields.resubmission', { returnObjects: true })}
                            companyId={insurer.module.companyId}
                            disabled={disabled}
                            moduleRole={ModuleRole.Signing}
                            name="reSubmissionApprovalModuleId"
                            addNoneOption
                        />
                    </Col>

                    {showCommentsField && (
                        <Col {...colSpan}>
                            <FormFields.SwitchField
                                {...t('insurerDetails:fields.showCommentsField', { returnObjects: true })}
                                disabled={disabled}
                                name="showCommentsField"
                                {...yesNoSwitch}
                                required
                            />
                        </Col>
                    )}

                    <Col {...colSpan}>
                        <FormFields.SwitchField
                            {...t('insurerDetails:fields.active', { returnObjects: true })}
                            disabled={disabled}
                            name="isActive"
                            {...yesNoSwitch}
                            required
                        />
                    </Col>
                    <Col {...colSpan}>
                        <FormFields.DisplayFieldWithUTCOffset
                            {...t('common:fields.updated', { returnObjects: true, offset })}
                            value={updated}
                        />
                    </Col>
                </Row>
            </Panel>
        </CollapsibleWrapper>
    );
};

export default EditInsurerDetailsForm;
